import React, { memo } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { AlertTriangle, Calendar, CheckCircle, Clock, LucideIcon, Mail, PaintRoller, User, UserPlus, Wrench } from 'lucide-react';
import { WorkOrderWithBuilding, WorkOrderStatus, workOrderPriorityColors, workOrderPriorityLabels, getAssignees, getDelegations } from '../../../types/workOrders';
import { daysFromToday, isOverdueDelmoment } from '../../../Helpers/projectBoard';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';

interface DelmomentRowProps {
    workOrder: WorkOrderWithBuilding;
    /** Today as YYYY-MM-DD, so due-date wording is consistent across the board. */
    today: string;
    getUserName: (uid: string) => string;
    onClick: (workOrder: WorkOrderWithBuilding) => void;
    onAssign?: (workOrder: WorkOrderWithBuilding) => void;
    onSetDueDate?: (workOrder: WorkOrderWithBuilding) => void;
}

/**
 * Assignment text as on the Förvaltning task card: one name (externals
 * suffixed "(extern)"), or "N tilldelade" for several.
 */
function assignmentDisplay(workOrder: WorkOrderWithBuilding, getUserName: (uid: string) => string): { text: string; external: boolean } | null {
    const assigneeUids = Object.keys(getAssignees(workOrder));
    const delegateUids = Object.keys(getDelegations(workOrder));
    const total = assigneeUids.length + delegateUids.length;
    if (total === 0) return null;
    if (total === 1) {
        if (assigneeUids.length === 1) return { text: getUserName(assigneeUids[0]), external: false };
        return { text: `${getUserName(delegateUids[0])} (extern)`, external: true };
    }
    return { text: `${total} tilldelade`, external: delegateUids.length > 0 };
}

/** Where the delmoment came from, with the Förvaltning card icon for that kind. */
function originDisplay(workOrder: WorkOrderWithBuilding): { Icon: LucideIcon; label: string } {
    if (workOrder.sourceIssueKey) return { Icon: AlertTriangle, label: 'Från felanmälan' };
    if (workOrder.maintenanceTemplateId) return { Icon: PaintRoller, label: 'Från underhåll' };
    return { Icon: Wrench, label: 'Ärende' };
}

/** Due date wording: the date, plus how late when overdue. */
function dueDisplay(workOrder: WorkOrderWithBuilding, today: string): string {
    if (!workOrder.dueDate) return 'Inget slutdatum';
    const date = formatDateForDisplay(workOrder.dueDate);
    const days = daysFromToday(today, workOrder.dueDate);
    return days < 0 ? `${date} (${-days} d försenat)` : date;
}

/**
 * One delmoment inside an expanded project card — a compact version of the
 * Förvaltning task card: title (after an icon for its origin) with the
 * priority pill, then a detail line
 * with the due date (red when overdue) and the assignee, and the assign /
 * due-date quick actions on the right.
 */
const DelmomentRow: React.FC<DelmomentRowProps> = memo(({
    workOrder,
    today,
    getUserName,
    onClick,
    onAssign,
    onSetDueDate
}) => {
    const completed = workOrder.status === WorkOrderStatus.COMPLETED;
    const overdue = isOverdueDelmoment(workOrder, today);
    const assignment = assignmentDisplay(workOrder, getUserName);
    const origin = originDisplay(workOrder);

    const handleClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.delmoment-row__action')) return;
        onClick(workOrder);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter') onClick(workOrder);
    };

    const stateClass = completed ? ' delmoment-row--completed' : overdue ? ' delmoment-row--overdue' : '';

    return (
        <div
            className={`delmoment-row${stateClass}`}
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div className="d-flex justify-content-between align-items-start gap-2">
                <div className="fw-medium delmoment-row__title">
                    <span className="delmoment-row__origin" title={origin.label}>
                        <origin.Icon size={14} aria-hidden="true" />
                        <span className="visually-hidden">{origin.label}: </span>
                    </span>
                    <span>{workOrder.title}</span>
                </div>
                {!completed && (
                    <Badge bg={workOrderPriorityColors[workOrder.priority]} className="priority-badge">
                        {workOrderPriorityLabels[workOrder.priority]}
                    </Badge>
                )}
            </div>
            <div className="mt-1 d-flex align-items-center flex-wrap gap-2 small text-muted">
                {completed ? (
                    <span className="project-board-done-pill">
                        <CheckCircle size={12} aria-hidden="true" />
                        {workOrder.completedAt ? `Slutförd: ${formatDateForDisplay(workOrder.completedAt)}` : 'Slutförd'}
                    </span>
                ) : (
                    <span className={`d-flex align-items-center gap-1${overdue ? ' text-danger' : ''}`}>
                        <Clock size={12} />
                        {dueDisplay(workOrder, today)}
                    </span>
                )}
                {assignment && (
                    <span className="d-flex align-items-center gap-1 delmoment-row__assignee">
                        {assignment.external ? <Mail size={12} /> : <User size={12} />}
                        {assignment.text}
                    </span>
                )}
                {!completed && (onAssign || onSetDueDate) && (
                    <span className="ms-auto d-inline-flex gap-2 delmoment-row__actions">
                        {onSetDueDate && (
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted delmoment-row__action"
                                style={{ lineHeight: 1 }}
                                onClick={(e) => { e.stopPropagation(); onSetDueDate(workOrder); }}
                                title={workOrder.dueDate ? 'Ändra slutdatum' : 'Sätt slutdatum'}
                            >
                                <Calendar size={16} />
                            </Button>
                        )}
                        {onAssign && (
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted delmoment-row__action"
                                style={{ lineHeight: 1 }}
                                onClick={(e) => { e.stopPropagation(); onAssign(workOrder); }}
                                title={assignment ? 'Ändra tilldelning' : 'Tilldela'}
                            >
                                <UserPlus size={16} />
                            </Button>
                        )}
                    </span>
                )}
            </div>
        </div>
    );
});

DelmomentRow.displayName = 'DelmomentRow';

export default DelmomentRow;
